import React from 'react'

import { ChevronRightIcon } from '@inno-ui/icons'
import { useForm, useStore } from '@tanstack/react-form'
import {
  AuthCheckbox,
  AuthInput,
  ErrorAlert,
  SavedUser
} from 'modules/auth/components'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useLoginMutation } from 'shared/api/auth/requests'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { DEFAULT_REMEMBER_ME } from 'shared/constants/user'
import { LocalSelectableUser } from 'shared/types/user'
import { getFieldError } from 'shared/utils'
import { lsRemoveItem, lsSetItem } from 'shared/utils/local-storage'

import { selectedUserViewSchema } from '../../SelectedUserView.schema'
import styles from './SelectedUserLoginForm.module.scss'

interface SelectedUserLoginFormProps {
  user: LocalSelectableUser
}

export const SelectedUserLoginForm: React.FC<SelectedUserLoginFormProps> = ({
  user
}) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login'
  })

  const loginMutation = useLoginMutation()
  const form = useForm({
    defaultValues: {
      password: '',
      remember_me: DEFAULT_REMEMBER_ME
    },
    validators: {
      onMount: selectedUserViewSchema,
      onChange: selectedUserViewSchema
    },
    onSubmit: ({ value }) => {
      if (FEATURE_FLAGS.SHOW_REMEMBER_ME) {
        try {
          if (value.remember_me) {
            lsSetItem(LocalStorageKey.REMEMBER_ME, true)
          } else {
            lsRemoveItem(LocalStorageKey.REMEMBER_ME)
          }
        } catch {
          // Swallow storage errors to avoid blocking login
        }
      }
      loginMutation.mutate({
        email: user.email,
        password: value.password,
        remember_me: value.remember_me
      })
    }
  })

  const canSubmit = useStore(form.store, state => state.canSubmit)
  const isSubmitted = useStore(
    form.store,
    state => state.submissionAttempts > 0
  )
  const errorMessage =
    loginMutation.error?.response?.status === 401 ? t('invalid_password') : ''

  return (
    <>
      <SavedUser user={user} size='big' />
      <form
        className={styles.form}
        onSubmit={e => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className={styles.password}>
          <form.Field name='password'>
            {field => (
              <AuthInput
                name='password'
                type='password'
                placeholder={t('login.password_placeholder')}
                value={field.state.value}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                actionButton={{
                  show: canSubmit,
                  icon: ChevronRightIcon,
                  isLoading: loginMutation.isPending,
                  type: 'submit'
                }}
              />
            )}
          </form.Field>
          <ErrorAlert message={errorMessage} />
          <div className={styles.rememberMe}>
            {FEATURE_FLAGS.SHOW_REMEMBER_ME && (
              <form.Field name='remember_me'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <AuthCheckbox
                      labelClassName={styles.rememberMeCheckbox}
                      label={t('login.remember_me')}
                      checked={field.state.value}
                      onChange={e => {
                        field.handleChange(e.target.checked)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            )}
            <Link
              to={`/forgot-password?user=${user.id}`}
              className={styles.forgotPassword}
            >
              {t('login.forgot_password')}
            </Link>
          </div>
        </div>
      </form>
    </>
  )
}
